/**
 * Which sessions the backend is going to withhold, and which one the trainer just rescued.
 *
 * ── The rule, and where it lives on the other side ───────────────────────────────────────────
 * `Services/ScheduleGenerator.cs` `GenerateCycle`, lines 189-199: a generated occurrence whose
 * EFFECTIVE date falls before `ScheduleContext.EarliestDate` is skipped — not pushed forward, so
 * no other date moves. `EarliestDate` is set by the initial assignment alone, to the plan's own
 * `StartDate`.
 *
 * In practice that strikes cycle 1 and nothing else, because cycle N is a whole week later than
 * cycle N-1 and cycle 1 is anchored in the start date's calendar WEEK: a plan that trains on
 * Monday, started on a Wednesday, generates its first Monday two days before it begins. **But the
 * rule as written judges every occurrence**, and this module does too, for a reason the trainer
 * can reach: the assignable date window's floor is TODAY, not the start date, so a trainer may
 * legally drag a session from week 4 onto a date before the plan starts. The server would drop it
 * silently. Here it goes dashed and comes out of the count, before anything is sent.
 *
 * ── It is judged on the EFFECTIVE date, so a move can rescue one ─────────────────────────────
 * The backend's own comment says so in as many words: "a trainer who moved that occurrence onto or
 * past the start date has rescued it". That is the whole reason the projection lets a struck chip
 * be dragged — it is not decoration, it is the one repair available for a mid-week start.
 *
 * ── Why this is not in `assignSchedule.ts` ───────────────────────────────────────────────────
 * That module is one half of a two-runtime algorithm pinned by the backend's own fixture, and the
 * fixture pins PATTERN DATES: it is the generator's date arithmetic, replayed. Withholding is a
 * different question asked afterwards, about the same occurrences, and folding it in would mean
 * the parity replay could no longer compare like with like.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { atMidnight, isoDayOfWeek, parseIsoDate } from '~/shared/lib/calendarDate';

import { coordinateKey, type ScheduleOccurrence } from './assignSchedule';
import type { OccurrenceOverride } from './assignRequest';
import type { Translate } from './assignRefusalCopy';

/** The date an occurrence actually falls on: the trainer's move if there is one, else the pattern. */
export function effectiveDate(
  occurrence: ScheduleOccurrence,
  overrides: ReadonlyMap<string, OccurrenceOverride>,
): Date {
  return overrides.get(coordinateKey(occurrence))?.date ?? occurrence.patternDate;
}

/**
 * Does this date reach the client, given where the plan starts?
 *
 * Both ends are normalised to midnight before the comparison, because the start date arrives from
 * a picker and an occurrence's date from the generator mirror, and one of them carrying a time
 * would make a session on the start date itself withheld in the afternoon and kept in the morning.
 */
export function reachesClient(date: Date, startDate: Date): boolean {
  return atMidnight(date).getTime() >= atMidnight(startDate).getTime();
}

/** What the projection needs to know about the withheld set. */
export interface PreStartDrops {
  /** Coordinate keys the server will skip. Empty on a plan that starts on its own first weekday. */
  readonly withheld: ReadonlySet<string>;
  /** How many — the number the helper sentence under the start field names. */
  readonly count: number;
  /**
   * The ISO weekdays (1..7) the struck sessions sit on, ascending and de-duplicated.
   *
   * The sentence names DAYS, not sessions: "poniedziałkowy Push z tego tygodnia nie odbędzie się"
   * reads as one fact where "1 sesja, 1 sesja" reads as two. The caller declines them — see
   * `assignRefusalCopy`'s weekday tables, which exist because Polish inflects a weekday differently
   * in "w poniedziałek" and "z poniedziałku".
   */
  readonly weekdays: readonly number[];
}

export const NO_DROPS: PreStartDrops = { withheld: new Set<string>(), count: 0, weekdays: [] };

/**
 * Every occurrence the start date strikes out, with the trainer's moves already applied.
 *
 * `startDate` is `null` before the trainer picks one, and the honest answer then is that nothing is
 * struck yet rather than that everything is: there is no floor to judge against, and the projection
 * is not drawn at all.
 */
export function preStartDrops(
  occurrences: readonly ScheduleOccurrence[],
  overrides: ReadonlyMap<string, OccurrenceOverride>,
  startDate: Date | null,
): PreStartDrops {
  if (startDate === null) return NO_DROPS;

  const withheld = new Set<string>();
  const weekdays = new Set<number>();

  for (const occurrence of occurrences) {
    const date = effectiveDate(occurrence, overrides);
    if (reachesClient(date, startDate)) continue;
    withheld.add(coordinateKey(occurrence));
    // The weekday the session LANDS on, which after a move is not the one it was programmed for.
    weekdays.add(isoDayOfWeek(date));
  }

  return { withheld, count: withheld.size, weekdays: [...weekdays].sort((a, b) => a - b) };
}

/**
 * The sentence under the start field: what choosing this date costs, or that it costs nothing.
 *
 * Here rather than in the component because every word of it is a DECISION — which of three
 * sentences applies, which declension table each weekday is read from, how a list of them is
 * joined — and none of it is reachable to a test from inside JSX. The component renders the
 * string; it makes none of these choices.
 *
 * Before a date is picked the sentence names the weekdays the program trains on, which is what the
 * dots under the grid mean. After one is picked it says what the mid-week rule strikes, which is
 * the whole reason variant D puts a hand-built picker here rather than a date input.
 */
export function startHelpSentence(
  input: {
    /** The chosen date as "YYYY-MM-DD", or `null` before the trainer picks one. */
    readonly startDate: string | null;
    readonly drops: PreStartDrops;
    /** The ISO weekdays the plan programmes — the dots, named in words. */
    readonly programmedWeekdays: readonly number[];
  },
  t: Translate,
): string {
  const start = parseIsoDate(input.startDate);
  if (start === null) {
    return t('web:assign.start.trainsOn', {
      weekdays: joinWeekdays(input.programmedWeekdays, 'plural', t),
    });
  }

  const on = t(`web:weekdays.accusative.${String(isoDayOfWeek(start))}`);
  return input.drops.count === 0
    ? t('web:assign.start.nothingDropped', { weekday: on })
    : t('web:assign.start.dropped', {
        count: input.drops.count,
        weekday: on,
        weekdays: joinWeekdays(input.drops.weekdays, 'accusative', t),
      });
}

/**
 * A list of weekdays as Polish writes one: "poniedziałki, środy i piątki".
 *
 * Not `Intl.ListFormat`: the weekday NAMES come from a finite declension table (Polish inflects
 * „środa" to „środę" after „w" and to „środy" in a plural list, and neither is derivable from the
 * `Intl` nominative), so the joining is done beside them rather than half here and half there.
 */
function joinWeekdays(
  weekdays: readonly number[],
  table: 'accusative' | 'plural',
  t: Translate,
): string {
  const names = weekdays.map((weekday) => t(`web:weekdays.${table}.${String(weekday)}`));
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] ?? '';
  const last = names[names.length - 1] ?? '';
  const head = names.slice(0, -1);
  if (head.length === 1) return t('web:assign.listJoin.pair', { first: head[0] ?? '', second: last });
  return t('web:assign.listJoin.more', {
    head: head.join(t('web:assign.listJoin.separator')),
    last,
  });
}
